import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'

const inputClassName = 'block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'

export default function TrainingDetailsSection({
	form,
	setField,
	setForm,
	certificationOptions = [],
	trainingNameOptions = [],
	showTrainingNameError = false,
	onTrainingNameErrorClear,
}) {
	const { t } = useTranslation()

	const statusOptions = [
		{ value: 'new', label: t('trainings.form.statusOptions.new') },
		{ value: 'assigned', label: t('trainings.form.statusOptions.assigned') },
		{ value: 'completed', label: t('trainings.form.statusOptions.completed') },
		{ value: 'approved', label: t('trainings.form.statusOptions.approved') },
		{ value: 'paid', label: t('trainings.form.statusOptions.paid') },
	]

	const selectedTrainingNameOption = form.nazwa
		? { value: form.nazwa, label: form.nazwa }
		: null
	const selectedStatusOption = statusOptions.find(option => option.value === form.status) || statusOptions[0]
	const selectedCertificateIds = Array.isArray(form.certificates)
		? form.certificates.map(item => item.certificateId)
		: []
	const selectedCertificationOptions = certificationOptions.filter(option =>
		selectedCertificateIds.includes(option.value)
	)

	const handleCertificationChange = value => {
		const certificates = Array.isArray(value)
			? value.map(option => ({
				certificateId: option.value,
				certificateName: option.label || '',
			}))
			: []
		setForm(prev => ({
			...prev,
			certificates,
			requiredCertificationIds: certificates.map(item => item.certificateId),
		}))
	}

	const clearTrainingNameError = () => {
		onTrainingNameErrorClear?.()
	}

	return (
		<div className="rounded-lg border border-gray-200 p-4">
			<h3 className="text-sm font-semibold text-gray-900">{t('trainings.details.title')}</h3>

			<div className="mt-4 space-y-4">
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('trainings.form.fields.trainingName')} *</span>
					<CreatableSelect
						name="nazwa"
						options={trainingNameOptions}
						value={selectedTrainingNameOption}
						onChange={value => {
							setField('nazwa', value?.value || '')
							clearTrainingNameError()
						}}
						onCreateOption={value => {
							setField('nazwa', value)
							clearTrainingNameError()
						}}
						placeholder={t('trainings.form.placeholders.trainingName')}
						formatCreateLabel={value => t('trainings.form.messages.createTrainingName', { value })}
						noOptionsMessage={() => t('trainings.form.messages.noTrainingNames')}
						className="react-select"
						classNamePrefix="react-select"
						isClearable
					/>
					{showTrainingNameError && (
						<span className="text-sm text-red-600">{t('trainings.form.validation.trainingNameRequired')}</span>
					)}
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('trainings.form.fields.requiredCertifications')}</span>
					<Select
						isMulti
						name="certifications"
						options={certificationOptions}
						value={selectedCertificationOptions}
						onChange={handleCertificationChange}
						placeholder={t('trainings.form.placeholders.requiredCertifications')}
						className="react-select"
						classNamePrefix="react-select"
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('trainings.form.fields.status')}</span>
					<Select
						name="status"
						options={statusOptions}
						value={selectedStatusOption}
						onChange={value => setField('status', value?.value || 'new')}
						className="react-select"
						classNamePrefix="react-select"
						isSearchable={false}
					/>
				</label>
				</div>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('trainings.form.fields.trainingDate')} *</span>
					<input
						type="date"
						className={inputClassName}
						value={form.data_rozpoczecia}
						onChange={e => setField('data_rozpoczecia', e.target.value)}
						required
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('trainings.form.fields.trainingEndDate')} *</span>
					<input
						type="date"
						className={inputClassName}
						value={form.data_zakonczenia}
						onChange={e => setField('data_zakonczenia', e.target.value)}
						required
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('trainings.form.fields.startTime')}</span>
					<input
						type="time"
						className={inputClassName}
						value={form.startTime}
						onChange={e => setField('startTime', e.target.value)}
					/>
				</label>

				<label className="flex flex-col gap-1">
					<span className="text-sm font-medium text-gray-700">{t('trainings.form.fields.endTime')}</span>
					<input
						type="time"
						className={inputClassName}
						value={form.endTime}
						onChange={e => setField('endTime', e.target.value)}
					/>
				</label>
				</div>
			</div>
		</div>
	)
}
